import React, { useState, useRef, useEffect } from "react";
import "./FaceRecognition.css";
import * as faceapi from "face-api.js";
import { signInWithFace } from "../../api/auth";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { setUserDetails } from "../../Redux/Slices/userDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import { reaunthenticateWithFace } from "../../api/auth";
import { setActiveComponent } from "../../Redux/Slices/activeComponentSlice";
import { setDashboardActiveComponent } from "../../Redux/Slices/dashboardActiveComponent";
import { registerFace, getUser } from "../../api/auth";
import { setAuthTokens } from "../../api/index";
import { getUserId, getToken } from "../../api/index";
import { getRedirectPath, clearRedirectPath } from "../../utils/authRedirect";

function FaceRecognition({ isUserVerified, onSuccess, onCancel }) {
    const [error, setError] = useState('');
    const [faceError, setFaceError] = useState(null);
    const [snapshotTaken, setSnapshotTaken] = useState(false);
    const [base64Image, setBase64Image] = useState('');
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [hasPermission, setHasPermission] = useState(false);
    const [isModelLoaded, setIsModelLoaded] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState('');
    const [buttonText, setButtonText] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const currentActiveComponent = useSelector((state) => state.activeComponent.activeComponent);
    console.log("currentActiveComponent", currentActiveComponent);

    const handleVerificationMessage = () => {
        if (isUserVerified === 'notUploaded') {
            setVerificationMessage('No Government Id verification status available, please upload a valid government document');
            setButtonText('Verify Now');
        } else if (isUserVerified === 'pending') {
            setVerificationMessage('Government Id verification status is pending, please wait for the verification to be completed');
            setButtonText('Check Status');
        } else if (isUserVerified === 'rejected') {
            setVerificationMessage('Government Id verification status is rejected, please upload a valid government document');
            setButtonText('Upload Again');
        }
    }

    const handleButtonClick = () => {
        if (buttonText === 'Verify Now') {
            navigate('/profile-details/edit-name');
        } else if (buttonText === 'Check Status') {
            navigate('/profile-details/request-status');
        } else if (buttonText === 'Upload Again') {
            navigate('/profile-details/edit-name');
        }
    }

    const blobToBase64 = (blob, stripPrefix = true) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                const result = reader.result;
                if (typeof result === 'string') {
                    const base64 = stripPrefix ? result.split(',')[1] : result;
                    resolve(base64);
                } else {
                    reject(new Error("Invalid FileReader result"));
                }
            };

            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        handleVerificationMessage();
        if (isUserVerified === 'verified') {
            const loadModels = async () => {
                try {
                    await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
                    console.log("Face API models loaded");
                    setIsModelLoaded(true);
                } catch (error) {
                    console.error("Error loading models:", error);
                    setError("Failed to load face detection models");
                }
            };
            loadModels();
        }
    }, [isUserVerified]);

    useEffect(() => {
        const startCamera = async () => {
            if (!videoRef.current || !isModelLoaded) return;

            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        facingMode: "user"
                    }
                });
                setStream(mediaStream);
                setHasPermission(true);
                setError("");

                videoRef.current.srcObject = mediaStream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play().then(() => {
                        console.log("🎥 Video playing — starting face detection...");
                        detectFace();
                    });
                };
            } catch (err) {
                console.log("Camera access error:", err);
                setError("Camera access denied or not available.");
                setHasPermission(false);
            }
        };

        if (videoRef.current && isModelLoaded) {
            startCamera();
        }
    }, [videoRef, isModelLoaded]);

    const detectFace = async (retryCount = 0, maxRetries = 20) => {
        if (!videoRef.current || snapshotTaken || !isModelLoaded || isUserVerified !== 'verified') return;

        try {
            const result = await faceapi.detectSingleFace(
                videoRef.current,
                new faceapi.TinyFaceDetectorOptions()
            );

            if (result) {
                if (!snapshotTaken) {
                    console.log("Face detected! Taking snapshot...");
                    setSnapshotTaken(true);
                    setBase64Image('');
                    takeSnapshotAndSignIn();
                }
            } else {
                if (retryCount < maxRetries) {
                    if (retryCount % 5 === 0) {
                        // Log every 5 tries to avoid spamming
                        console.log("No face detected. Retrying...");
                    }
                    setTimeout(() => detectFace(retryCount + 1, maxRetries), 1000);
                } else {
                    setFaceError("No face detected, please ensure your face is visible to the camera and try again.");
                    setSnapshotTaken(false);
                }
            }
        } catch (err) {
            console.error("Face detection error:", err);
            setFaceError("Face detection failed. Please check your camera and try again.");
            setSnapshotTaken(false);
        }
    };

    const takeSnapshotAndSignIn = () => {
        const video = videoRef.current;
        if (!video) return;

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(async (blob) => {
                if (blob) {
                    try {
                        const base64String = await blobToBase64(blob);
                        console.log("New base64 image generated");
                        // Call API directly with new base64String
                        if (pathname === "/signin-face-recognition") {
                            try {
                                const response = await signInWithFace({
                                    faceImage: base64String
                                });
                                console.log("signInWithFace API response:", response);
                                localStorage.setItem("userId", response.data.userDetails.userId);
                                setAuthTokens(response.data);
                                dispatch(setUserDetails(response.data.userDetails));
                                const userData = response.data.userDetails;
                                if (userData?.firstName === null || userData?.lastName === null) {
                                    dispatch(setActiveComponent("enterName"));
                                    navigate('/signup', { replace: true });
                                  } else if (userData?.gender === null) {
                                    dispatch(setActiveComponent("enterPersonalDetails"));
                                    navigate('/signup', { replace: true });
                                  } else if (userData?.addresses.length === 0) {
                                    dispatch(setActiveComponent("enterAddress"));
                                    navigate('/signup', { replace: true });
                                  } else if (userData.faceId === null) {
                                    dispatch(setActiveComponent("faceRecognition1"));
                                    navigate('/signup', { replace: true });
                                  } else {
                                    const target = getRedirectPath();
                                    clearRedirectPath();
                                    navigate(target, { replace: true });
                                  }
                            }
                            catch (error) {
                                console.error("sign in with Face API error:", error);
                                setFaceError("Face Recognition Failed");
                            }
                        } else if (currentActiveComponent === "faceRecognition1") {
                            try {
                                const response = await registerFace({
                                    faceImage: base64String,
                                    userId: getUserId(),
                                }, getToken());

                                console.log("registerFace API response:", response);
                                try {
                                    const updateResponse = await getUser(getUserId(), getToken());
                                    console.log("getUser API response:", updateResponse.data);
                                    dispatch(setUserDetails(updateResponse.data));

                                } catch (updateError) {
                                    console.error("Update User API error:", updateError);
                                }

                                dispatch(setActiveComponent("signUpSuccessfull"));
                            } catch (error) {
                                console.error("Register Face API error:", error);
                                setFaceError("Face Recognition Failed");
                            }
                        }
                        else {
                            try {
                                const response = await reaunthenticateWithFace({
                                    faceImage: base64String
                                });
                                console.log("reauthenticateWithFace API response:", response);
                                if (response.data.userId === getUserId()) {
                                    onSuccess();
                                }
                                else {
                                    setFaceError("Face Recognition Failed");
                                }
                            }
                            catch (error) {
                                console.error("reauthenticateWithFace API error:", error);
                                setFaceError("Face Recognition Failed");
                            }
                        }

                    } catch (err) {
                        console.error("Error converting to base64:", err);
                        setFaceError("Error processing image");
                        setSnapshotTaken(false);
                    }
                } else {
                    console.error("Failed to create blob from canvas.");
                    setFaceError("Error processing image");
                    setSnapshotTaken(false);
                }
            }, 'image/jpeg');
        }
    };

    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    const handleTryAgainClick = async () => {
        setSnapshotTaken(false);
        setBase64Image('');
        setFaceError('');
        takeSnapshotAndSignIn();
    }

    return (
        <div className="faceRecognitionVideoContainer">
            {isUserVerified !== 'verified' ? (
                <div className="verificationMessage" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#fff',
                    textAlign: 'center',
                    padding: '20px'
                }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '24px' }}>{verificationMessage}</h3>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <button
                            onClick={() => {
                                handleButtonClick();
                            }}
                            style={{
                                backgroundColor: '#3BB3AE',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            {buttonText}
                        </button>
                        <button
                            onClick={() => {
                                // Handle cancel logic here
                                console.log("Cancel clicked");
                                if (onCancel) {
                                    onCancel();
                                }
                            }}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#ccc',
                                border: '1px solid #666',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : error ? (
                <div className="errorMessage">{error}</div>
            ) : (
                <>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '10px',
                            transform: 'scaleX(-1)',
                            overflow: 'hidden'
                        }}
                    />
                    {faceError && (
                        <div className="faceErrorContainer">
                            <div className="faceErrorMessage">{faceError}</div>
                            <button className="tryAgainButton"
                                onClick={() => handleTryAgainClick()}
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                    <div className="blueCornersOverlay">
                        <div className="corner top-left" />
                        <div className="corner top-right" />
                        <div className="corner bottom-left" />
                        <div className="corner bottom-right" />
                    </div>
                </>
            )}
        </div>
    )
}

export default FaceRecognition;